import { useMemo, createContext } from "react";

import { Form, Flex, Input, Button, Card, List, Collapse } from "antd";

import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import TrashIcon from "@/icons/TrashIcon";
import PlusIcon from "@/icons/PlusIcon";
import ChartBarIcon from "@/icons/ChartBarIcon";
import ChartPieIcon from "@/icons/ChartPieIcon";
import ChartLineIcon from "@/icons/ChartLineIcon";

import DragHandle from "@/components/ui/DragHandle";
import RichText from "@/components/ui/RichText";
import CustomSelect from "@/components/inputs/CustomSelect";

import TableColumnsList from "./TableColumnsList";
import TableParametersList from "./TableParametersList";

const SortableListItemContext = createContext({});

const SECTION_TITLES = {
  text: "Текстовый раздел",
  table: "Раздел с таблицей",
  chart: "Раздел с графиком",
};

const CHART_TYPES = [
  {
    value: "bar",
    label: (
      <Flex align="center" gap={5}>
        <ChartBarIcon />
        <span>Столбчатая диаграмма</span>
      </Flex>
    ),
  },
  {
    value: "line",
    label: (
      <Flex align="center" gap={5}>
        <ChartLineIcon />
        <span>Линейная диаграмма</span>
      </Flex>
    ),
  },
  {
    value: "pie",
    label: (
      <Flex align="center" gap={5}>
        <ChartPieIcon />
        <span>Круговая диаграмма</span>
      </Flex>
    ),
  },
];

const TextSectionsList = ({ name = "text", form }) => {
  const SortableListItem = (props) => {
    const { itemKey, style, ...rest } = props;
    const {
      attributes,
      listeners,
      setNodeRef,
      setActivatorNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: itemKey });
    const listStyle = {
      ...style,
      transform: CSS.Translate.toString(transform),
      transition,
      ...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
    };
    const memoizedValue = useMemo(
      () => ({ setActivatorNodeRef, listeners, attributes }),
      [setActivatorNodeRef, listeners, attributes],
    );
    return (
      <SortableListItemContext.Provider value={memoizedValue}>
        <List.Item {...rest} ref={setNodeRef} style={listStyle} />
      </SortableListItemContext.Provider>
    );
  };

  // Компонент секции (текстовая, табличная, график)
  const SectionComponent = ({ name: idx, remove, ...restField }) => {
    // form.text[idx].type
    const sectionType = form.getFieldValue([name, idx, "type"]) || "text";
    const title = SECTION_TITLES[sectionType] || "";

    return (
      <Card
        size="small"
        style={{ width: "100%" }}
        title={
          <Flex align="center" gap={10}>
            <DragHandle context={SortableListItemContext} />
            {title}
          </Flex>
        }
        extra={
          <Button
            type="text"
            aria-label="Удалить секцию"
            icon={<TrashIcon />}
            onClick={() => remove(idx)}
          ></Button>
        }
      >
        {(() => {
          switch (sectionType) {
            case "table":
              return <TableContent restField={restField} idx={idx} />;
            case "chart":
              return <ChartContent restField={restField} idx={idx} />;
            default:
              return <TextContent restField={restField} idx={idx} />;
          }
        })()}
      </Card>
    );
  };

  const TextContent = ({ restField, idx }) => {
    return (
      <Flex vertical gap={5}>
        <Form.Item name={[idx, "type"]} hidden>
          <Input value="text" />
        </Form.Item>

        <label className="label">Текст:</label>
        <Form.Item
          {...restField}
          name={[idx, "text"]}
          rules={[
            {
              validator: (_, value) => {
                const plain = (value || "")
                  .replace(/<br\s*\/?>/gi, "\n")
                  .replace(/&nbsp;/g, " ")
                  .replace(/<[^>]*>/g, "")
                  .trim();
                return plain
                  ? Promise.resolve()
                  : Promise.reject(new Error("Заполните поле"));
              },
            },
          ]}
          style={{ marginBottom: 0 }}
          valuePropName="value"
          trigger="onChange"
        >
          <RichText />
        </Form.Item>
      </Flex>
    );
  };

  const TableContent = ({ restField, idx }) => {
    return (
      <Flex vertical gap={20}>
        <Form.Item name={[idx, "type"]} hidden>
          <Input value="table" />
        </Form.Item>

        <Flex vertical gap={5}>
          <label className="label">Колонки таблицы:</label>

          <TableColumnsList fieldName={idx} />
        </Flex>

        <Flex vertical gap={5}>
          <label className="label">SQL запрос:</label>
          <Form.Item
            {...restField}
            name={[idx, "text"]}
            rules={[
              {
                required: true,
                message: "Заполните поле",
              },
            ]}
            style={{ marginBottom: 0 }}
          >
            <Input.TextArea autoSize={{ minRows: 3, maxRows: 12 }} allowClear />
          </Form.Item>
        </Flex>

        <Collapse
          bordered={false}
          items={[
            {
              key: "subtext",
              label: "Подзапрос (необязательно)",
              children: (
                <Form.Item
                  {...restField}
                  name={[idx, "subtext"]}
                  style={{ marginBottom: 0 }}
                >
                  <Input.TextArea
                    autoSize={{ minRows: 3, maxRows: 12 }}
                    allowClear
                  />
                </Form.Item>
              ),
            },
          ]}
        ></Collapse>
      </Flex>
    );
  };

  const ChartContent = ({ restField, idx }) => {
    return (
      <Flex vertical gap={20}>
        <Form.Item name={[idx, "type"]} hidden>
          <Input value="chart" />
        </Form.Item>

        <div>
          <Flex vertical gap={5}>
            <label className="label">Тип графика:</label>
            <Form.Item name={[idx, "chartType"]}>
              <CustomSelect
                placeholder="Тип"
                options={CHART_TYPES}
                allowClear={false}
              />
            </Form.Item>
          </Flex>
          <Flex vertical gap={5}>
            <label className="label">Заголовок:</label>
            <Form.Item
              name={[idx, "title"]}
              rules={[
                {
                  required: true,
                  message: "Заполните поле",
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Flex>

          <Flex vertical gap={5}>
            <label className="label">Легенда:</label>

            <TableParametersList fieldName={[idx, "columns"]} />
          </Flex>
        </div>

        <Flex vertical gap={5}>
          <label className="label">SQL запрос:</label>
          <Form.Item
            {...restField}
            name={[idx, "text"]}
            rules={[
              {
                required: true,
                message: "Заполните поле",
              },
            ]}
            style={{ marginBottom: 0 }}
          >
            <Input.TextArea autoSize={{ minRows: 3, maxRows: 12 }} allowClear />
          </Form.Item>
        </Flex>
      </Flex>
    );
  };

  return (
    <Form.List name={name}>
      {(fields, { add, remove, move }) => (
        <>
          <DndContext
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={({ active, over }) => {
              if (!over || active.id === over.id) return;

              const oldIndex = fields.findIndex((f) => f.key === active.id);
              const newIndex = fields.findIndex((f) => f.key === over.id);
              if (oldIndex === -1 || newIndex === -1) return;

              move(oldIndex, newIndex);
            }}
            id="sections-drag-sorting"
          >
            <SortableContext
              items={fields.map((item) => item.key)}
              strategy={verticalListSortingStrategy}
            >
              <List
                grid={{ gutter: 10, column: 1 }}
                dataSource={fields}
                // locale={{ emptyText: "Добавьте секцию" }}
                renderItem={(item) => {
                  const { key, ...itemProps } = item;
                  return (
                    <SortableListItem key={key} itemKey={key}>
                      <SectionComponent {...itemProps} remove={remove} />
                    </SortableListItem>
                  );
                }}
              ></List>
            </SortableContext>
          </DndContext>

          <Flex gap={10}>
            <Button
              block
              type="dashed"
              icon={<PlusIcon />}
              aria-label="Добавить текст"
              onClick={() =>
                add({
                  type: "text",
                  text: "",
                })
              }
            >
              Добавить текст
            </Button>
            <Button
              block
              type="dashed"
              icon={<PlusIcon />}
              aria-label="Добавить таблицу"
              onClick={() =>
                add({
                  type: "table",
                  columns: [],
                  text: "",
                })
              }
            >
              Добавить таблицу
            </Button>

            <Button
              block
              type="dashed"
              icon={<PlusIcon />}
              aria-label="Добавить график"
              onClick={() =>
                add({
                  type: "chart",
                  chartType: CHART_TYPES[0].value,
                  columns: [],
                  text: "",
                })
              }
            >
              Добавить график
            </Button>
          </Flex>
        </>
      )}
    </Form.List>
  );
};

export default TextSectionsList;
